'use client';
// TODO: Fix React 19 compiler lint errors. See https://react.dev/learn/react-compiler
/* eslint-disable react-hooks/set-state-in-effect */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import cn from '@utils/cn';
import { useClickOutside } from '@lib/hooks/useClickOutside';
import useEscapeClick from '@lib/hooks/useEscapeClick';
import useIsMobile from '@lib/hooks/useIsMobile';
import { AnimatePresence } from 'framer-motion';
import SelectMobile from './SelectMobile';
import SelectDesktop from './SelectDesktop';
import { LabelValueHint } from '@lib/types';
import TextBlock, { TextVariant } from '@ui/Text/TextBlock';
import { StaticIcon } from '@ui/Icon/StaticIcon/StaticIcon';
import IconType from '@ui/Icon/StaticIcon/IconType';
import { DESIGN_SIZE_TYPE_TYPES } from '@components/Templates/utils/constants';

export type SelectProps = {
  items: LabelValueHint[];
  selectedItems?: (string | number)[];
  setSelectedItems?: (items: (string | number)[]) => void;
  label: string;
  isMultiple: boolean;
  showSearch?: boolean;
};
export default function Select({
  items: initialItems,
  selectedItems,
  label,
  setSelectedItems,
  isMultiple,
  showSearch = false,
}: SelectProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isMenuOpened, setIsMenuOpened] = useState(false);
  const { isEscapeClicked, setIsEscapeClicked } = useEscapeClick();
  const [search, setSearch] = useState('');
  const [items, setItems] = useState(initialItems);
  const isMobile = useIsMobile();

  const clickOutside = useCallback(() => {
    if (!isMobile) {
      setIsMenuOpened(false);
    }
  }, [isMobile]);

  useClickOutside(ref, clickOutside);

  useEffect(() => {
    if (isEscapeClicked && !isMobile) {
      clickOutside();
      setIsEscapeClicked(false);
    }
  }, [isEscapeClicked, setIsEscapeClicked, isMobile, clickOutside]);

  useEffect(() => {
    if (!search.trim()) {
      setItems(initialItems);
    }
    setItems(initialItems.filter((x) => x.label.toLowerCase().includes(search.toLowerCase())));
  }, [search, setItems, initialItems]);

  function close() {
    setIsMenuOpened(false);
  }

  function itemClick(value: string | number) {
    if (!setSelectedItems) {
      return;
    }

    if (!isMultiple) {
      close();
    }

    if (!selectedItems?.length) {
      return setSelectedItems([value]);
    }

    if (selectedItems.includes(value)) {
      return setSelectedItems(selectedItems.filter((x) => x !== value));
    }

    if (!isMultiple) {
      return setSelectedItems([value]);
    }

    return setSelectedItems([...selectedItems, value]);
  }

  function getTitle() {
    if (isMultiple) {
      return selectedItems?.length ? `${label} (${selectedItems.length})` : label;
    }
    return selectedItems?.length ? DESIGN_SIZE_TYPE_TYPES.find((x) => x.value === selectedItems[0])?.label : label;
  }

  return (
    <div ref={ref} className="relative w-full sm:w-auto lg:min-w-[8rem] xl:min-w-[11rem] 2xl:min-w-[12rem]">
      <div
        onClick={() => setIsMenuOpened((val) => !val)}
        className={cn('hover:bg-input h-9 w-full cursor-pointer whitespace-nowrap rounded-t-lg border px-2', {
          'rounded-b-lg': !isMenuOpened,
          'hover:bg-background bg-background text-foreground': selectedItems?.length,
        })}
      >
        <TextBlock
          variant={TextVariant.TextLabel}
          className="flex h-full select-none items-center justify-between gap-2 pl-1 sm:gap-8"
        >
          {getTitle()}
          <StaticIcon icon={isMenuOpened ? IconType.chevronUp : IconType.chevronDown} />
        </TextBlock>
      </div>

      {isMenuOpened && !isMobile && (
        <SelectDesktop
          items={items}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          isMenuOpened={isMenuOpened}
          itemClick={itemClick}
          search={search}
          setSearch={setSearch}
          showSearch={showSearch}
          isMultiple={isMultiple}
        />
      )}
      <AnimatePresence>
        {isMenuOpened && isMobile && (
          <SelectMobile
            items={items}
            selectedItems={selectedItems}
            setSelectedItems={setSelectedItems}
            label={label}
            itemClick={itemClick}
            close={close}
            setSearch={setSearch}
            search={search}
            showSearch={showSearch}
            isMultiple={isMultiple}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
